/**
 * CSV lead-import modal.
 *
 * Accepts a UTF-8 CSV, forwards to POST /api/admin/leads/import-csv,
 * and reports imported / skipped counts. Staff-only.
 */
import React, { useState } from 'react';
import { Upload, X, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

export default function LeadCsvImportModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [service, setService] = useState('probound');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setResult(null); };

  const submit = async () => {
    if (!file) { toast.error('Choose a CSV file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('default_service_type', service);
      const res = await fetch(`${API_URL}/api/admin/leads/import-csv`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(formatApiError(payload) || 'Import failed');
      setResult(payload);
      toast.success(`Imported ${payload.imported} lead${payload.imported === 1 ? '' : 's'}`);
      onImported?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Upload className="w-5 h-5" /> Import Leads from CSV
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="csv-import-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!result ? (
          <div className="space-y-4">
            <div className="text-xs text-gray-600 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed">
              <p className="font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Expected columns (any of these work)</p>
              <p><span className="font-medium">Required:</span> <code className="bg-white px-1 rounded">name</code> or <code className="bg-white px-1 rounded">customer_name</code></p>
              <p className="mt-1"><span className="font-medium">Optional:</span> email, phone, address, notes, timeline, size, service (probound/proepoxy/prodecor), source, installer_email</p>
            </div>

            <div>
              <Label className="text-xs font-semibold" htmlFor="csv-default-service">Default section (used when a row doesn&apos;t specify one)</Label>
              <select
                id="csv-default-service"
                data-testid="csv-default-service"
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
              >
                <option value="probound">ProBound</option>
                <option value="proepoxy">ProEpoxy</option>
                <option value="prodecor">ProDecor</option>
              </select>
            </div>

            <div>
              <Label className="text-xs font-semibold" htmlFor="csv-file">CSV file</Label>
              <input
                id="csv-file"
                data-testid="csv-file"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-xs mt-1 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#7A0B7A]/10 file:text-[#7A0B7A] hover:file:bg-[#7A0B7A]/20"
              />
              {file && (
                <p className="text-[11px] text-gray-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={uploading || !file} className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white" data-testid="csv-submit">
                <Upload className="w-4 h-4 mr-1.5" /> {uploading ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Imported {result.imported} lead{result.imported === 1 ? '' : 's'}</span>
            </div>
            {result.unknown_headers?.length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="font-semibold flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Ignored columns</p>
                <p className="font-mono">{result.unknown_headers.join(', ')}</p>
              </div>
            )}
            {result.skipped?.length > 0 && (
              <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md p-3 max-h-48 overflow-auto">
                <p className="font-semibold flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> {result.skipped.length} skipped</p>
                <ul className="space-y-0.5">
                  {result.skipped.map((s) => (
                    <li key={`skip-${s.row}-${s.reason}`}>Row {s.row}: {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={reset}>Import another</Button>
              <Button onClick={onClose} className="bg-gray-900 hover:bg-black text-white">Done</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
